import {
  createFileRoute,
  redirect,
  stripSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import jwtDecode from 'jwt-decode';
import { queryFleetMembers } from 'src/api/queries/fleet';
import type { RmClaims } from 'src/api/queries/user';
import { EnterpriseDevicesPage } from 'src/apps/fleet/app/pages/EnterpriseDevicesPage';
import { authHelpers } from 'src/utils/authHelpers';
import { AccessDeniedError } from 'src/utils/errors';
import { userHelpers } from 'src/utils/userHelpers';
import { z } from 'zod';

const defaultValues = {
  filter: '',
  device: '',
  devices: [],
  members: [],
  integrations: [],
  'device-software-versions': [],
  compliance: [],
  'device-verified': [],
};

const routeSearchSchema = z.object({
  device: z.string().default(defaultValues.device),
  devices: z.array(z.string()).default(defaultValues.devices),
  members: z.array(z.string()).default(defaultValues.members),
  filter: z.string().default(defaultValues.filter),
  integrations: z.array(z.string()).default(defaultValues.integrations),
  'device-software-versions': z
    .array(z.string())
    .default(defaultValues['device-software-versions']),
  compliance: z.array(z.string()).default(defaultValues.compliance),
  'device-verified': z
    .array(z.string())
    .default(defaultValues['device-verified']),
});

export const Route = createFileRoute(
  '/_auth/enterprise/_with-org/_layout/devices'
)({
  validateSearch: zodValidator(routeSearchSchema),
  search: { middlewares: [stripSearchParams(defaultValues)] },
  async beforeLoad({ context }) {
    const auth = await context.auth;

    if (!auth.user) {
      throw redirect({ to: '/login' });
    }

    const accessToken = await authHelpers.getAccessTokenSilently(auth);
    const claims = jwtDecode<RmClaims>(accessToken);

    if (!userHelpers.hasPermissionReadMembers(claims)) {
      throw new AccessDeniedError(
        'You do not have permission to view this page. Please contact your IT admin to request access.'
      );
    }
  },
  loader({ context }) {
    void context.queryClient.ensureQueryData(queryFleetMembers());
  },
  component: () => <EnterpriseDevicesPage />,
});
